/**
 * Maker-checker work items — the pending approvals the dashboard lists and the
 * detail behind each one.
 *
 * Every approval tour starts on the dashboard, opens a record and walks the
 * approve / reject controls, so each module needs both a list row and a
 * matching detail record keyed by the same transaction id.
 */

import { TOUR_AWARDS } from './tour-sample-awards';
import { TOUR_USER_GROUPS, TOUR_USERS } from './tour-sample-people';
import { TOUR_TEMPLATE_FIELDS, TOUR_TEMPLATES } from './tour-sample-templates';

/** Workflow status ids the dashboard's Maker / Checker tabs filter by. */
export const TOUR_WORKFLOW_STATUS = {
  makerInProgress: 1,
  pendingCheckerApproval: 3,
  /** Sits in the Maker bucket with `makerInProgress` — see the master fixture. */
  pendingRevision: 4,
  approved: 7,
  rejected: 8,
} as const;

/** The fields every maintenance row carries, whatever the module. */
interface ApprovalBase {
  id: number;
  txnId: string;
  moduleName: string;
  workflowActionName: string;
  workflowStatusId: number;
  txnStatus: string;
  createdBy: string;
  createdAt: string;
  lastMaker: string;
  lastMakerDatetime: string;
  lastChecker: string | null;
  lastCheckerDatetime: string | null;
  makerRemarks: string | null;
  checkerRemarks: string | null;
}

/**
 * Every module keeps rows in BOTH dashboard buckets.
 *
 * The Maker / Checker switch defaults differently per grid (some tabs settle
 * their status after the first render), so a module whose rows all sit in one
 * bucket shows "No data available" whenever the other tab wins. One maker row
 * per module keeps either tab populated; the rest await approval, which is
 * what the approval tours are about.
 */
const isMakerRow = (index: number) => index === 0;

const approvalBase = (
  index: number,
  moduleName: string,
  prefix: string,
  action: 'Create' | 'Update' = 'Create'
): ApprovalBase => ({
  id: 1000 + index,
  txnId: `${prefix}-${String(index + 1).padStart(6, '0')}`,
  moduleName,
  workflowActionName: action,
  workflowStatusId: isMakerRow(index)
    ? TOUR_WORKFLOW_STATUS.makerInProgress
    : TOUR_WORKFLOW_STATUS.pendingCheckerApproval,
  txnStatus: isMakerRow(index)
    ? 'Maker in progress'
    : 'Pending Checker Approval',
  createdBy: 'Arun Kumar',
  createdAt: `2025-09-${String(10 + index).padStart(2, '0')}T09:30:00Z`,
  lastMaker: 'Arun Kumar',
  lastMakerDatetime: `2025-09-${String(10 + index).padStart(2, '0')}T09:30:00Z`,
  lastChecker: null,
  lastCheckerDatetime: null,
  makerRemarks: 'Submitted for review.',
  checkerRemarks: null,
});

export interface TourUserApproval extends ApprovalBase {
  fullName: string;
  employeeId: string;
  email: string;
  role: string;
  userGroupName: string;
  userId: number;
}

export const TOUR_USER_APPROVALS: readonly TourUserApproval[] = Object.freeze(
  TOUR_USERS.slice(0, 4).map((user, index) => ({
    ...approvalBase(
      index,
      'User Management',
      'TOUR-TXN-USR',
      index === 3 ? 'Update' : 'Create'
    ),
    fullName: user.fullName,
    employeeId: user.employeeId,
    email: user.email,
    role: user.userGroupName,
    userGroupName: user.userGroupName,
    userId: user.id,
  }))
);

export interface TourUserGroupApproval extends ApprovalBase {
  groupName: string;
  userGroupId: number;
  dataAccessLevels: string;
  /**
   * The submitted record, as the approval screens read it.
   *
   * These pages do not render the list row — they transform `newData` (and
   * `oldData` for an edit), and that payload is PascalCase on the wire.
   * Handing them the camelCase row leaves every field showing "-".
   */
  newData: Record<string, unknown>;
}

const getGroupSecondarySelections = (dataAccessLevels: string) => {
  if (dataAccessLevels === 'Company') {
    return { Company: [{ Id: 1 }] };
  }
  if (dataAccessLevels === 'Department') {
    return { Department: [{ Id: 202 }] };
  }
  return {};
};

export const TOUR_USER_GROUP_APPROVALS: readonly TourUserGroupApproval[] =
  Object.freeze(
    TOUR_USER_GROUPS.slice(0, 3).map((group, index) => {
      const base = approvalBase(index, 'User Management', 'TOUR-TXN-GRP');
      return {
        ...base,
        groupName: group.groupName,
        userGroupId: group.id,
        dataAccessLevels: group.dataAccessLevels,
        newData: {
          GroupName: group.groupName,
          DataAccessLevels: group.dataAccessLevels,
          // Keyed by parameter table, each entry a list of selected ids.
          SecondarySelections: getGroupSecondarySelections(
            group.dataAccessLevels
          ),
          PermissionIds: [101, 102, 103, 201, 202, 301, 401],
          StatusId: 1,
          CreatedBy: base.createdBy,
          Remarks: base.makerRemarks,
          IsDraft: false,
          TxnId: base.txnId,
        },
      };
    })
  );

export interface TourAwardApproval extends ApprovalBase {
  fullName: string;
  planName: string;
  awardNo: string;
  awardedShares: number;
}

export const TOUR_AWARD_APPROVALS: readonly TourAwardApproval[] = Object.freeze(
  TOUR_AWARDS.slice(0, 4).map((award, index) => ({
    ...approvalBase(index, 'Award Management', 'TOUR-TXN-AWD'),
    fullName: award.employeeName,
    planName: award.planName,
    awardNo: award.awardNo,
    awardedShares: award.awardedShares,
  }))
);

export interface TourTemplateApproval extends ApprovalBase {
  fullName: string;
  category: string;
  templateId: number;
  templateName: string;
  /** The submitted template, PascalCase as the approval screen reads it. */
  newData: Record<string, unknown>;
}

export const TOUR_TEMPLATE_APPROVALS: readonly TourTemplateApproval[] =
  Object.freeze(
    TOUR_TEMPLATES.slice(0, 3).map((template, index) => ({
      ...approvalBase(index, 'Templates', 'TOUR-TXN-TPL'),
      fullName: template.templateName,
      category: template.categoryName,
      templateId: template.templateId,
      templateName: template.templateName,
      // The template approval screen renders the submitted payload, not the
      // list row, and reads it PascalCase — without it every field is "N/A".
      newData: {
        TemplateName: template.templateName,
        TemplateTypeId: template.templateTypeId,
        CategoryId: template.categoryId,
        Subject: template.subject,
        BodyContent: template.bodyContent,
        SelectedFields: template.selectedFields
          .map(placeholder =>
            TOUR_TEMPLATE_FIELDS.find(
              field => field.placeholder === placeholder
            )
          )
          .filter(field => field !== undefined)
          .map((field, order) => ({ CategoryFieldId: field.id, Order: order })),
      },
    }))
  );

/** Parameter change requests, listed per table by the Parameters dashboard. */
export const getTourParameterApprovals = (tableName: string) =>
  [0, 1].map(index => ({
    ...approvalBase(
      index,
      'Parameters',
      `TOUR-TXN-${tableName.toUpperCase().slice(0, 4)}`
    ),
    category: tableName,
    tableName,
    fullName: `${tableName} change ${index + 1}`,
    name: `${tableName} change ${index + 1}`,
  }));

/**
 * The maintenance rows for a module, keyed by the endpoint segment the
 * dashboard's tab config uses (`maintenance/users`, `maintenance/awards`, …).
 * A Map rather than an object literal: the keys are URL segments, not
 * identifiers, and `user-groups` is not a legal property name here.
 */
export const TOUR_APPROVALS_BY_TYPE = new Map<string, readonly ApprovalBase[]>([
  ['users', TOUR_USER_APPROVALS],
  ['user-groups', TOUR_USER_GROUP_APPROVALS],
  ['awards', TOUR_AWARD_APPROVALS],
  ['templates', TOUR_TEMPLATE_APPROVALS],
]);

/** Every approval row across the modules, for the by-txnId detail lookup. */
const ALL_APPROVALS: ApprovalBase[] = [
  ...TOUR_APPROVALS_BY_TYPE.values(),
].flat();

/**
 * The detail behind an approval, looked up by transaction id. A txnId the
 * fixtures do not know falls back to the first user approval so an approval
 * tour that arrived by an unexpected route still has something to show.
 */
export const getTourApprovalDetail = (txnId: string) => {
  const match =
    ALL_APPROVALS.find(item => item.txnId === txnId) ?? TOUR_USER_APPROVALS[0];

  // Modules that render the submitted payload supply their own `newData` in
  // the shape their screen transforms (PascalCase on the wire); the rest fall
  // back to the row itself.
  const submitted =
    (match as { newData?: Record<string, unknown> }).newData ?? match;

  return {
    ...match,
    // The approval pages read the record under a handful of names depending on
    // the module, so the detail carries the same body under each of them.
    data: match,
    details: match,
    oldData: null,
    newData: submitted,
    fieldChanges: Object.entries(match)
      .filter(([key]) => !key.startsWith('last') && key !== 'id')
      .slice(0, 6)
      .map(([field, value]) => ({
        fieldName: field,
        displayName: field,
        oldValue: null,
        newValue: value === null ? '' : String(value),
      })),
  };
};
